import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  BookOpen, Cloud, LayoutDashboard, Users, FolderOpen, FileText,
  Search, Shield, ShieldCheck, Eye, Upload, GitBranch, MessageSquare,
  Download, Clock, Webhook, BarChart3, Settings,
  ChevronRight, CheckCircle, AlertCircle, Info, Lightbulb, Lock,
  ArrowRight, Zap, RefreshCw, Layers, Code, ThumbsUp, Sparkles,
  ChevronDown, ChevronUp, HelpCircle
} from 'lucide-react';

// ────────────────────────────────────────────────────────
// Helper components
// ────────────────────────────────────────────────────────

const RoleBadge = ({ role }) => {
  const styles = {
    reader: 'bg-slate-100 text-slate-700 border border-slate-200',
    editor: 'bg-amber-100 text-amber-700 border border-amber-200',
    admin: 'bg-red-100 text-red-700 border border-red-200',
  };
  const icons = { reader: Eye, editor: Shield, admin: ShieldCheck };
  const Icon = icons[role];
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${styles[role]}`}>
      <Icon className="h-3 w-3" />
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
};

const Callout = ({ type = 'info', title, children }) => {
  const styles = {
    info: { bg: 'bg-blue-50 border-blue-200', icon: Info, iconColor: 'text-blue-500', titleColor: 'text-blue-800' },
    tip: { bg: 'bg-emerald-50 border-emerald-200', icon: Lightbulb, iconColor: 'text-emerald-500', titleColor: 'text-emerald-800' },
    warning: { bg: 'bg-amber-50 border-amber-200', icon: AlertCircle, iconColor: 'text-amber-500', titleColor: 'text-amber-800' },
    admin: { bg: 'bg-red-50 border-red-200', icon: ShieldCheck, iconColor: 'text-red-500', titleColor: 'text-red-800' },
  };
  const s = styles[type];
  const Icon = s.icon;
  return (
    <div className={`flex gap-3 p-4 rounded-lg border ${s.bg} my-4`}>
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${s.iconColor}`} />
      <div>
        {title && <p className={`font-semibold text-sm mb-1 ${s.titleColor}`}>{title}</p>}
        <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-5">
    <div className="shrink-0 w-7 h-7 rounded-full bg-[#2B8FD8] text-white flex items-center justify-center text-sm font-bold">
      {number}
    </div>
    <div>
      <p className="font-semibold text-slate-900 mb-1">{title}</p>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  </div>
);

const SectionHeading = ({ id, icon: Icon, title, roles = [] }) => (
  <div id={id} className="scroll-mt-24 pt-12 pb-4 border-b border-slate-200 mb-6">
    <div className="flex items-center gap-3 mb-2">
      {Icon && <div className="p-2 bg-blue-50 rounded-lg"><Icon className="h-5 w-5 text-[#2B8FD8]" /></div>}
      <h2 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">{title}</h2>
    </div>
    {roles.length > 0 && (
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-slate-500 mr-1">Accessible by:</span>
        {roles.map(r => <RoleBadge key={r} role={r} />)}
      </div>
    )}
  </div>
);

// ────────────────────────────────────────────────────────
// UI Mockups (CSS-based screenshots with annotations)
// ────────────────────────────────────────────────────────

const MockupFrame = ({ title, children, height = 'auto' }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden shadow-lg my-6 bg-white">
    {/* Browser chrome */}
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-400"></div>
        <div className="w-3 h-3 rounded-full bg-amber-400"></div>
        <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
      </div>
      <div className="flex-1 bg-white rounded border border-slate-200 px-3 py-1 text-xs text-slate-500 font-mono mx-4">
        72.61.213.241/{title}
      </div>
    </div>
    <div style={{ minHeight: height }}>
      {children}
    </div>
  </div>
);

const Annotation = ({ number, label, position = 'right' }) => (
  <div className={`absolute flex items-center gap-2 ${position === 'left' ? 'flex-row-reverse' : ''}`} style={{ zIndex: 10 }}>
    <div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-md shrink-0">
      {number}
    </div>
    <div className={`bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-md ${position === 'left' ? 'mr-1' : 'ml-1'}`}>
      {label}
    </div>
  </div>
);

// ────────────────────────────────────────────────────────
// Section content components
// ────────────────────────────────────────────────────────

const MockDashboard = () => (
  <div className="flex h-64 overflow-hidden text-[11px] relative">
    {/* Sidebar */}
    <div className="w-44 bg-white border-r border-slate-200 flex flex-col shrink-0">
      <div className="h-10 border-b border-slate-200 px-3 flex items-center gap-1.5">
        <Cloud className="h-4 w-4 text-[#2B8FD8]" />
        <span className="font-bold text-slate-800 text-xs">CloudFactory</span>
      </div>
      <div className="p-2 space-y-0.5 flex-1">
        {[
          { icon: LayoutDashboard, label: 'Dashboard', active: true },
          { icon: Users, label: 'Customers' },
          { icon: FolderOpen, label: 'Projects' },
          { icon: FileText, label: 'Documentation' },
          { icon: Search, label: 'Search' },
        ].map(({ icon: Icon, label, active }) => (
          <div key={label} className={`flex items-center gap-2 px-2 py-1.5 rounded text-[10px] ${active ? 'bg-blue-50 text-[#2B8FD8] font-semibold' : 'text-slate-600'}`}>
            <Icon className="h-3 w-3" /> {label}
          </div>
        ))}
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="text-[9px] text-slate-400 uppercase font-semibold px-2 mb-1">Admin</div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-[10px] text-slate-600">
            <ShieldCheck className="h-3 w-3" /> Admin Panel
          </div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-[10px] text-slate-600">
            <BarChart3 className="h-3 w-3" /> Analytics
          </div>
        </div>
      </div>
      <div className="border-t border-slate-200 p-2">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-[#2B8FD8] flex items-center justify-center text-white text-[9px] font-bold">A</div>
          <div>
            <div className="font-semibold text-[9px] text-slate-800">Admin User</div>
            <div className="text-[8px] text-red-600 font-semibold">Admin</div>
          </div>
        </div>
      </div>
    </div>
    {/* Main */}
    <div className="flex-1 bg-slate-50 p-3 overflow-hidden">
      <div className="font-bold text-sm text-slate-900 mb-2">Dashboard</div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[{ l: 'Customers', v: '5', c: 'text-[#2B8FD8]' }, { l: 'Projects', v: '12', c: 'text-emerald-600' }, { l: 'Docs', v: '28', c: 'text-amber-600' }].map(({ l, v, c }) => (
          <div key={l} className="bg-white rounded border border-slate-200 p-2">
            <div className="text-[9px] text-slate-500">{l}</div>
            <div className={`text-lg font-bold ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded border border-slate-200 p-2">
        <div className="font-semibold text-[10px] text-slate-700 mb-1.5">Quick Actions</div>
        <div className="space-y-1">
          <div className="border border-slate-200 rounded px-2 py-1 text-[9px] text-slate-600 flex items-center gap-1"><Plus className="h-2.5 w-2.5" /> Add New Customer</div>
          <div className="border border-slate-200 rounded px-2 py-1 text-[9px] text-slate-600 flex items-center gap-1"><Plus className="h-2.5 w-2.5" /> Create Project</div>
          <div className="bg-[#2B8FD8] rounded px-2 py-1 text-[9px] text-white flex items-center gap-1"><Plus className="h-2.5 w-2.5" /> Upload Documentation</div>
        </div>
      </div>
    </div>
    {/* Annotations */}
    <div className="absolute top-12 left-1 pointer-events-none">
      <div className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow">1</div>
    </div>
    <div className="absolute top-14 left-44 pointer-events-none">
      <div className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow">2</div>
    </div>
    <div className="absolute bottom-14 left-44 pointer-events-none">
      <div className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow">3</div>
    </div>
  </div>
);

const Plus = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>;

const MockProjectDetail = () => (
  <div className="flex flex-col h-80 text-[11px] overflow-hidden">
    <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between">
      <div>
        <div className="font-bold text-sm text-slate-900">My Integration Project</div>
        <div className="text-[10px] text-slate-500">BankConnect · v7 · Updated 2 days ago</div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="border border-slate-200 text-slate-600 rounded px-2 py-1 text-[9px] flex items-center gap-1"><RefreshCw className="h-2.5 w-2.5" />Regenerate</div>
        <div className="border border-slate-200 text-slate-600 rounded px-2 py-1 text-[9px] flex items-center gap-1"><Download className="h-2.5 w-2.5" />Word</div>
        <div className="border border-slate-200 text-slate-600 rounded px-2 py-1 text-[9px] flex items-center gap-1"><Download className="h-2.5 w-2.5" />PDF</div>
        <div className="border border-slate-200 text-slate-600 rounded px-2 py-1 text-[9px] flex items-center gap-1"><Clock className="h-2.5 w-2.5" />History</div>
        <div className="border border-emerald-400 text-emerald-600 rounded px-2 py-1 text-[9px] flex items-center gap-1"><Webhook className="h-2.5 w-2.5" />Auto-Sync</div>
      </div>
    </div>
    <div className="flex flex-1 overflow-hidden">
      {/* Doc list */}
      <div className="w-44 border-r border-slate-200 bg-slate-50 p-2 overflow-auto">
        <div className="text-[9px] text-slate-500 uppercase font-semibold mb-1.5 px-1">Documents</div>
        {[
          { title: 'Integration Guide', v: 'v7', latest: true },
          { title: 'API Reference', v: 'v3', latest: false },
          { title: 'Setup Guide', v: 'v2', latest: true },
        ].map(({ title, v, latest }) => (
          <div key={title} className={`rounded p-1.5 mb-1 cursor-pointer flex items-start justify-between ${latest ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-200'}`}>
            <span className="text-[9px] text-slate-700 font-medium leading-tight">{title}</span>
            <span className="text-[8px] text-slate-400 ml-1 shrink-0">{v}</span>
          </div>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 p-3 overflow-auto bg-white">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-3">
          {['Full View', 'Customer View', 'Internal/Technical'].map((t, i) => (
            <div key={t} className={`px-2 py-1 text-[9px] rounded-t cursor-pointer ${i === 0 ? 'bg-blue-50 text-[#2B8FD8] border-b-2 border-[#2B8FD8] font-semibold' : 'text-slate-500'}`}>{t}</div>
          ))}
        </div>
        <div className="text-[10px] text-slate-800 space-y-1">
          <div className="font-bold text-xs">1. Introduction</div>
          <div className="text-slate-600 text-[9px]">This document describes the integration between BankConnect and the customer portal API...</div>
          <div className="font-bold text-xs mt-2">2. Authentication</div>
          <div className="text-slate-600 text-[9px]">All API calls require a Bearer token in the Authorization header...</div>
          <div className="bg-slate-900 text-emerald-400 rounded p-1.5 mt-1 text-[8px] font-mono">GET /api/v1/accounts<br />Authorization: Bearer {'{token}'}</div>
        </div>
      </div>
    </div>
    {/* Floating AI button */}
    <div className="absolute bottom-4 right-4 w-9 h-9 rounded-full bg-[#2B8FD8] flex items-center justify-center shadow-lg">
      <Sparkles className="h-4 w-4 text-white" />
    </div>
  </div>
);

const MockUploadPage = () => (
  <div className="p-4 bg-slate-50 h-56 overflow-hidden">
    <div className="font-bold text-sm text-slate-900 mb-3">Upload Documentation</div>
    <div className="flex gap-1 mb-3 border-b border-slate-200">
      {['File Upload', 'Azure DevOps', 'GitHub', 'Bitbucket', 'GitLab'].map((t, i) => (
        <div key={t} className={`px-2 py-1 text-[9px] cursor-pointer ${i === 0 ? 'border-b-2 border-[#2B8FD8] text-[#2B8FD8] font-semibold' : 'text-slate-500'}`}>{t}</div>
      ))}
    </div>
    <div className="bg-white rounded-lg border-2 border-dashed border-slate-300 p-6 text-center mb-3">
      <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1" />
      <div className="text-xs font-semibold text-slate-700">Drop your ZIP file here</div>
      <div className="text-[9px] text-slate-400 mt-0.5">or click to browse</div>
    </div>
    <div className="flex gap-2">
      <div className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-[9px] text-slate-500">Project name...</div>
      <div className="bg-[#2B8FD8] text-white rounded px-3 py-1.5 text-[9px] font-semibold">Generate Docs</div>
    </div>
  </div>
);

const MockChatPanel = () => (
  <div className="flex h-64 overflow-hidden">
    <div className="flex-1 bg-white p-3 text-[10px] relative">
      <div className="text-sm font-bold text-slate-800 mb-0.5">Integration Guide</div>
      <div className="text-[9px] text-slate-500 mb-3">Documentation content...</div>
      {/* Floating button */}
      <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-[#2B8FD8] flex items-center justify-center shadow-lg cursor-pointer">
        <Sparkles className="h-3.5 w-3.5 text-white" />
      </div>
    </div>
    {/* Chat panel */}
    <div className="w-56 bg-white border-l border-slate-200 flex flex-col shadow-xl">
      <div className="border-b border-slate-200 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[#2B8FD8]" />
          <span className="text-xs font-semibold text-slate-800">AI Assistant</span>
        </div>
      </div>
      <div className="border-b border-slate-200 p-2 flex gap-1">
        <div className="flex-1 bg-blue-50 text-[#2B8FD8] rounded px-2 py-1 text-[9px] font-semibold text-center">Ask Questions</div>
        <div className="flex-1 bg-white border border-slate-200 rounded px-2 py-1 text-[9px] text-slate-500 text-center">Request Changes</div>
      </div>
      <div className="flex-1 p-2 overflow-auto space-y-2">
        <div className="bg-slate-100 rounded-lg p-1.5 text-[9px] text-slate-700">How does authentication work in this API?</div>
        <div className="bg-blue-50 rounded-lg p-1.5 text-[9px] text-slate-700 ml-2">Authentication uses JWT Bearer tokens. Include the token in the Authorization header as <span className="font-mono bg-white px-0.5 rounded">Bearer {'{token}'}</span>. Tokens expire after 24 hours...</div>
      </div>
      <div className="p-2 border-t border-slate-200">
        <div className="border border-slate-200 rounded-lg px-2 py-1.5 text-[9px] text-slate-400">Ask a question...</div>
      </div>
    </div>
  </div>
);

const MockSearchPage = () => (
  <div className="p-4 bg-white h-56 overflow-hidden">
    <div className="font-bold text-sm text-slate-900 mb-3">Search Documentation</div>
    <div className="flex gap-2 mb-4">
      <div className="flex-1 border border-[#2B8FD8] rounded-lg px-3 py-2 text-[10px] text-slate-700 flex items-center gap-2">
        <Search className="h-3 w-3 text-slate-400" />
        retry logic authentication
      </div>
      <div className="bg-[#2B8FD8] text-white rounded-lg px-3 py-2 text-[9px] font-semibold">Search</div>
    </div>
    <div className="space-y-2">
      {[
        { title: 'BankConnect API Guide', section: 'Error Handling & Retry', snippet: '...exponential backoff is used when retrying failed auth requests...' },
        { title: 'Mule Integration Docs', section: 'Authentication Flow', snippet: '...retry logic triggers after 3 consecutive 401 responses...' },
      ].map(({ title, section, snippet }) => (
        <div key={title} className="bg-slate-50 border border-slate-200 rounded-lg p-2 hover:border-blue-200 cursor-pointer">
          <div className="font-semibold text-[10px] text-slate-800">{title}</div>
          <div className="text-[9px] text-[#2B8FD8] mt-0.5">§ {section}</div>
          <div className="text-[9px] text-slate-500 mt-0.5 italic">"{snippet}"</div>
        </div>
      ))}
    </div>
  </div>
);

const MockAdminPanel = () => (
  <div className="p-4 bg-slate-50 h-56 overflow-hidden">
    <div className="font-bold text-sm text-slate-900 mb-3">Admin Panel</div>
    <div className="flex gap-1 mb-3 border-b border-slate-200">
      {['Users', 'Delete Requests'].map((t, i) => (
        <div key={t} className={`px-3 py-1 text-[9px] cursor-pointer ${i === 0 ? 'border-b-2 border-[#2B8FD8] text-[#2B8FD8] font-semibold' : 'text-slate-500'}`}>{t}</div>
      ))}
    </div>
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      {[
        { name: 'Admin User', email: 'admin@company.co', role: 'admin' },
        { name: 'Jane Smith', email: 'jane@company.co', role: 'editor' },
        { name: 'Bob Reader', email: 'bob@company.co', role: 'reader' },
      ].map(({ name, email, role }) => (
        <div key={name} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-0">
          <div>
            <div className="text-[10px] font-semibold text-slate-800">{name}</div>
            <div className="text-[9px] text-slate-400">{email}</div>
          </div>
          <div className={`text-[8px] font-semibold px-2 py-0.5 rounded-full ${
            role === 'admin' ? 'bg-red-100 text-red-700' :
            role === 'editor' ? 'bg-amber-100 text-amber-700' :
            'bg-slate-100 text-slate-600'
          }`}>{role.charAt(0).toUpperCase() + role.slice(1)}</div>
        </div>
      ))}
    </div>
  </div>
);

const MockAnalytics = () => (
  <div className="p-4 bg-white h-56 overflow-hidden">
    <div className="flex items-center justify-between mb-3">
      <div className="font-bold text-sm text-slate-900">Analytics Dashboard</div>
      <div className="flex gap-1">
        {['7d','30d','90d'].map((d, i) => (
          <div key={d} className={`px-2 py-0.5 rounded text-[8px] font-semibold cursor-pointer ${i===1 ? 'bg-[#2B8FD8] text-white' : 'border border-slate-200 text-slate-500'}`}>{d}</div>
        ))}
      </div>
    </div>
    <div className="grid grid-cols-4 gap-2 mb-3">
      {[
        { l: 'Total Events', v: '1,234', c: 'text-[#2B8FD8]' },
        { l: 'Doc Views', v: '587', c: 'text-emerald-600' },
        { l: 'Searches', v: '234', c: 'text-amber-600' },
        { l: 'AI Queries', v: '413', c: 'text-purple-600' },
      ].map(({ l, v, c }) => (
        <div key={l} className="bg-slate-50 rounded border border-slate-200 p-2">
          <div className="text-[8px] text-slate-500">{l}</div>
          <div className={`text-sm font-bold ${c}`}>{v}</div>
        </div>
      ))}
    </div>
    <div className="bg-slate-50 rounded border border-slate-200 p-2">
      <div className="text-[9px] font-semibold text-slate-600 mb-1.5">Top Documentation Gaps (0-result searches)</div>
      {['webhook timeout handling', 'SSL certificate renewal', 'rate limit bypass'].map((q, i) => (
        <div key={q} className="flex items-center justify-between py-0.5">
          <span className="text-[9px] text-slate-600">"{q}"</span>
          <span className="text-[8px] text-red-500 font-semibold">{3 - i} searches</span>
        </div>
      ))}
    </div>
  </div>
);

// ────────────────────────────────────────────────────────
// Table of contents data
// ────────────────────────────────────────────────────────

const sections = [
  { id: 'intro', title: 'Introduction', icon: BookOpen },
  { id: 'roles', title: 'User Roles', icon: Shield },
  { id: 'first-login', title: 'First Login & Setup', icon: Zap },
  { id: 'dashboard', title: 'Dashboard', icon: LayoutDashboard },
  { id: 'customers', title: 'Managing Customers', icon: Users },
  { id: 'projects', title: 'Managing Projects', icon: FolderOpen },
  { id: 'uploading', title: 'Uploading & Importing Docs', icon: Upload },
  { id: 'viewing', title: 'Viewing Documentation', icon: FileText },
  { id: 'audience', title: 'Audience Views', icon: Layers },
  { id: 'ai-chat', title: 'AI Chat Assistant', icon: MessageSquare },
  { id: 'suggest', title: 'Requesting Changes (AI)', icon: Sparkles },
  { id: 'source-files', title: 'Source Files Browser', icon: Code },
  { id: 'versions', title: 'Version History & Diff', icon: GitBranch },
  { id: 'search', title: 'Search & Deep Links', icon: Search },
  { id: 'feedback', title: 'Providing Feedback', icon: ThumbsUp },
  { id: 'export', title: 'Exporting Documents', icon: Download },
  { id: 'webhooks', title: 'Webhook Auto-Sync', icon: Webhook },
  { id: 'analytics', title: 'Analytics Dashboard', icon: BarChart3 },
  { id: 'admin', title: 'Admin Panel', icon: ShieldCheck },
  { id: 'settings', title: 'Settings & Profile', icon: Settings },
  { id: 'quick-ref', title: 'Quick Reference', icon: HelpCircle },
];

// ────────────────────────────────────────────────────────
// Main UserGuidePage
// ────────────────────────────────────────────────────────

const UserGuidePage = () => {
  const { role } = useAuth();
  const [activeSection, setActiveSection] = useState('intro');
  const [tocOpen, setTocOpen] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    observerRef.current = observer;
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTocOpen(false);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white">
        {/* Hero banner */}
        <div className="bg-gradient-to-r from-slate-900 via-[#1e3a5f] to-[#2B8FD8] text-white py-12 px-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs><pattern id="dots" width="5" height="5" patternUnits="userSpaceOnUse"><circle cx="2.5" cy="2.5" r="0.5" fill="white" /></pattern></defs>
              <rect width="100" height="100" fill="url(#dots)" />
            </svg>
          </div>
          <div className="relative max-w-4xl">
            <div className="flex items-center gap-3 mb-3">
              <BookOpen className="h-8 w-8 text-blue-300" />
              <span className="text-blue-300 font-semibold text-sm uppercase tracking-wider">User Guide</span>
            </div>
            <h1 className="text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-3">CloudFactory Docs Hub</h1>
            <p className="text-blue-100 text-lg max-w-2xl leading-relaxed">
              Complete guide to generating, managing, and sharing AI-powered integration documentation. From uploading source code to exporting polished docs — everything you need to know.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1.5 text-sm">
                <Eye className="h-3.5 w-3.5 text-blue-200" /> Readers
              </div>
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1.5 text-sm">
                <Shield className="h-3.5 w-3.5 text-amber-300" /> Editors
              </div>
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1.5 text-sm">
                <ShieldCheck className="h-3.5 w-3.5 text-red-300" /> Admins
              </div>
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1.5 text-sm">
                <BookOpen className="h-3.5 w-3.5 text-green-300" /> 20 sections
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto flex gap-0">
          {/* Left TOC — desktop sticky */}
          <aside className="hidden xl:block w-72 shrink-0">
            <div className="sticky top-0 h-screen overflow-y-auto pt-8 pb-8 px-6 border-r border-slate-100">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Contents</div>
              <nav className="space-y-0.5">
                {sections.map(({ id, title, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === id
                        ? 'bg-blue-50 text-[#2B8FD8] font-semibold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${activeSection === id ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                    <span className="leading-tight">{title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile TOC */}
          <div className="xl:hidden fixed bottom-4 right-4 z-50">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              className="bg-[#2B8FD8] text-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2 text-sm font-semibold"
            >
              <BookOpen className="h-4 w-4" />
              Contents
              {tocOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            {tocOpen && (
              <div className="absolute bottom-12 right-0 w-64 bg-white border border-slate-200 rounded-xl shadow-xl p-3 max-h-80 overflow-y-auto">
                {sections.map(({ id, title }) => (
                  <button key={id} onClick={() => scrollTo(id)} className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 rounded-md">
                    {title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <main className="flex-1 max-w-3xl px-8 py-8 xl:border-l-0">

            {/* ── SECTION 1: Introduction ── */}
            <SectionHeading id="intro" icon={BookOpen} title="Introduction" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>CloudFactory Docs Hub</strong> is an AI-powered documentation platform that automatically generates, manages, and maintains technical documentation from your source code. It connects to your Git repositories and code archives to produce rich documentation that stays up-to-date as your codebase evolves.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
              {[
                { icon: Zap, title: 'AI Generation', desc: 'Upload source code and get professional documentation in minutes, powered by Claude & Gemini.', color: 'bg-purple-50 text-purple-600' },
                { icon: RefreshCw, title: 'Auto Sync', desc: 'Connect your Git repo via webhooks and docs update automatically on every code push.', color: 'bg-emerald-50 text-emerald-600' },
                { icon: MessageSquare, title: 'AI Chat', desc: 'Ask questions about your documentation or request specific changes using natural language.', color: 'bg-blue-50 text-[#2B8FD8]' },
              ].map(({ icon: Icon, title, desc, color }) => (
                <div key={title} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <Callout type="info" title="What you can do with CloudFactory Docs Hub">
              Generate documentation from ZIP files or Git repos (GitHub, Azure DevOps, Bitbucket, GitLab) · View audience-specific docs (customer-facing vs internal/technical) · Chat with AI about your docs · Export to Word or PDF · Search across all documentation · Set up auto-sync webhooks
            </Callout>

            {/* ── SECTION 2: User Roles ── */}
            <SectionHeading id="roles" icon={Shield} title="User Roles & Permissions" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-6">
              CloudFactory Docs Hub uses a three-tier role system. Your role determines what actions you can perform. You can see your current role in the bottom-left corner of the sidebar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[
                {
                  role: 'reader', badge: 'Reader', color: 'border-slate-300 bg-slate-50', icon: Eye,
                  title: 'Read-Only Access',
                  can: ['View all documentation', 'Use AI chat (Q&A mode)', 'Search documentation', 'View project details', 'Browse source files'],
                  cannot: ['Upload or import docs', 'Suggest changes', 'Delete anything', 'Manage users'],
                },
                {
                  role: 'editor', badge: 'Editor', color: 'border-amber-300 bg-amber-50', icon: Shield,
                  title: 'Create & Edit Access',
                  can: ['Everything a Reader can do', 'Upload & import docs from repos', 'Request AI changes', 'Configure webhooks', 'Request document deletion (pending admin approval)'],
                  cannot: ['Delete directly', 'Manage users or roles', 'View analytics'],
                },
                {
                  role: 'admin', badge: 'Admin', color: 'border-red-300 bg-red-50', icon: ShieldCheck,
                  title: 'Full Access',
                  can: ['Everything Editors can do', 'Delete documents directly', 'Manage user roles', 'Review delete requests', 'View analytics dashboard', 'Reset user passwords'],
                  cannot: [],
                },
              ].map(({ role, badge, color, icon: Icon, title, can, cannot }) => (
                <div key={role} className={`border-2 ${color} rounded-xl p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-5 w-5" />
                    <RoleBadge role={role} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
                  <div className="space-y-1.5">
                    {can.map(item => (
                      <div key={item} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                        {item}
                      </div>
                    ))}
                    {cannot.map(item => (
                      <div key={item} className="flex items-start gap-2 text-sm text-slate-400">
                        <div className="h-4 w-4 rounded-full border-2 border-slate-300 mt-0.5 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Callout type="tip" title="New users are automatically assigned the Reader role">
              The first user who registers on a fresh system becomes the Admin. All subsequent registrations are assigned Reader by default. Admins can change any user's role from the Admin Panel.
            </Callout>

            {/* ── SECTION 3: First Login ── */}
            <SectionHeading id="first-login" icon={Zap} title="First Login & Setup" roles={['reader', 'editor', 'admin']} />
            <Step number={1} title="Navigate to the login page">
              Go to <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono">http://72.61.213.241</code> — you'll be redirected to the login screen automatically.
            </Step>
            <Step number={2} title="Sign in or create an account">
              Enter your email and password, then click <strong>Sign In</strong>. If you're new, click <em>"Don't have an account? Sign up"</em> and fill in your name, email, and password.
            </Step>
            <Step number={3} title="Check your role">
              After logging in, look at the bottom-left of the sidebar. You'll see your name, email, and a coloured role badge. If you need a different role, contact your admin.
            </Step>
            <Step number={4} title="Explore the dashboard">
              The dashboard shows your key stats (customers, projects, documentation count) and quick-action buttons relevant to your role.
            </Step>
            <Callout type="warning" title="If you see 'Read-only access'">
              New users are assigned the Reader role by default. Contact your system administrator to request Editor access if you need to upload or modify documentation.
            </Callout>

            {/* ── SECTION 4: Dashboard ── */}
            <SectionHeading id="dashboard" icon={LayoutDashboard} title="Dashboard" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The dashboard is your home base. It gives you an at-a-glance view of the platform's key metrics and provides quick navigation to your most common tasks.
            </p>
            <MockupFrame title="dashboard">
              <MockDashboard />
            </MockupFrame>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
              <div className="flex gap-2"><div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold shrink-0">1</div><div><strong>Sidebar</strong> — Navigation between all major sections of the app. Admin-only links appear at the bottom.</div></div>
              <div className="flex gap-2"><div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold shrink-0">2</div><div><strong>Stats Cards</strong> — Total customers, projects, and documentation. Click any card to navigate to that section.</div></div>
              <div className="flex gap-2"><div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold shrink-0">3</div><div><strong>Quick Actions</strong> — Role-based shortcuts. Editors/Admins see upload, create buttons. Readers see a helpful note.</div></div>
            </div>

            {/* ── SECTION 5: Customers ── */}
            <SectionHeading id="customers" icon={Users} title="Managing Customers" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Customers are the top-level organisational unit. Each customer can have multiple projects. Think of customers as the organisations or clients whose software you're documenting.
            </p>
            <Step number={1} title="View all customers">
              Click <strong>Customers</strong> in the sidebar to see a list of all customers. Click any customer card to view their details and associated projects.
            </Step>
            <Step number={2} title="Add a new customer (Editor / Admin only)">
              Click the <strong>+ Add Customer</strong> button in the top right. Fill in the customer name and optional description, then click Save.
            </Step>
            <Step number={3} title="View customer details">
              Click on a customer name to open the Customer Detail page, which lists all projects for that customer. From here you can navigate directly to any project.
            </Step>
            <Step number={4} title="Delete a customer (Admin only)">
              On the Customer Detail page, the Delete button is available for admins. <strong>Warning:</strong> deleting a customer permanently removes all their projects, documentation, and related data.
            </Step>
            <Callout type="warning" title="Cascade deletion">
              Deleting a customer removes ALL associated projects, documentation, source files, chat history, webhooks, and jobs. This action cannot be undone.
            </Callout>

            {/* ── SECTION 6: Projects ── */}
            <SectionHeading id="projects" icon={FolderOpen} title="Managing Projects" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Projects belong to customers and group related documentation together. Each project can have multiple documentation documents (e.g., different versions or different areas of the codebase).
            </p>
            <Step number={1} title="Navigate to Projects">
              Click <strong>Projects</strong> in the sidebar or navigate via a customer's detail page.
            </Step>
            <Step number={2} title="Create a new project (Editor / Admin only)">
              Click <strong>+ New Project</strong> and fill in the project name, select the customer it belongs to, and optionally add a description and tags.
            </Step>
            <Step number={3} title="Open a project">
              Click any project card to open the Project Detail page — this is the main documentation viewer.
            </Step>
            <Step number={4} title="Delete a project (Admin only)">
              On the project detail page, admins can delete the project. This cascades to delete all documentation and associated data.
            </Step>

            {/* ── SECTION 7: Uploading & Importing ── */}
            <SectionHeading id="uploading" icon={Upload} title="Uploading & Importing Documentation" roles={['editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The Upload page lets you generate AI documentation from your source code. You can upload a ZIP file directly or import from a Git repository.
            </p>
            <MockupFrame title="documentation/upload">
              <MockUploadPage />
            </MockupFrame>
            <p className="text-slate-600 leading-relaxed mb-4">The upload page has <strong>5 tabs</strong> — one for each source type:</p>
            <div className="space-y-4 mb-6">
              {[
                { tab: 'File Upload', icon: Upload, desc: 'Upload a ZIP archive containing your source code. The AI will analyse all code files and generate comprehensive documentation.' },
                { tab: 'Azure DevOps', icon: GitBranch, desc: 'Enter your Azure DevOps repo URL and Personal Access Token (PAT). The system clones the repo and generates docs from the codebase.' },
                { tab: 'GitHub', icon: GitBranch, desc: 'Enter a GitHub repo URL and optionally a GitHub token (required for private repos). Supports the x-access-token auth format.' },
                { tab: 'Bitbucket', icon: GitBranch, desc: 'Enter your Bitbucket repo URL and app password. Supports both Bitbucket Cloud and Server.' },
                { tab: 'GitLab', icon: GitBranch, desc: 'Enter a GitLab repo URL and personal/project access token. Supports gitlab.com and self-hosted GitLab instances including nested groups.' },
              ].map(({ tab, icon: Icon, desc }) => (
                <div key={tab} className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{tab}</p>
                    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Callout type="tip" title="Token auto-save">
              After a successful import using a token, the system automatically saves your token securely (encrypted). Next time you import from the same repo, leave the token field blank — it will reuse your saved token automatically.
            </Callout>
            <Callout type="info" title="Job processing">
              Documentation generation runs as a background job. After clicking Generate, you'll see a progress indicator. This usually takes several minutes (Code Docs 8 to 12). You can navigate away and return later — the job will continue.
            </Callout>
            <Step number={1} title="Go to Documentation → Upload New">
              Click <strong>Documentation</strong> in the sidebar, then <strong>Upload New</strong> (Editor / Admin only).
            </Step>
            <Step number={2} title="Select the project">
              Choose the project this documentation belongs to from the dropdown.
            </Step>
            <Step number={3} title="Choose your import method">
              Click the relevant tab (File Upload, GitHub, etc.) and fill in the required details.
            </Step>
            <Step number={4} title="Enter a title">
              Give the documentation a descriptive name (e.g., "BankConnect API v3").
            </Step>
            <Step number={5} title="Click Generate">
              Click the Generate button and wait for the job to complete. You'll see live progress updates.
            </Step>

            {/* ── SECTION 8: Viewing Documentation ── */}
            <SectionHeading id="viewing" icon={FileText} title="Viewing Documentation" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The Project Detail page is the main documentation viewer. It features a Notion-style reading experience with a document sidebar, toolbar, auto-generated Table of Contents, and multiple view modes.
            </p>
            <MockupFrame title="projects/[id]">
              <MockProjectDetail />
            </MockupFrame>
            <div className="space-y-3 mb-4">
              {[
                { title: 'Document Sidebar (left)', desc: 'Lists all documents in the project. The currently selected document is highlighted in blue. Documents marked "(superseded)" are older versions that have been regenerated.' },
                { title: 'Toolbar (top right)', desc: 'Contains action buttons: Regenerate, Export Word, Export PDF, Version History, Feedback, Source Files, and Auto-Sync Webhooks.' },
                { title: 'View Tabs', desc: 'Three tabs — Full View, Customer View, and Internal/Technical. Customer and Internal views are audience-specific versions of the documentation.' },
                { title: 'Table of Contents', desc: 'Auto-generated from the document headings. Sticks to the right side as you scroll, allowing quick navigation within a long document.' },
                { title: 'AI Chat Button (✨)', desc: 'The floating sparkle button at the bottom-right opens the AI assistant panel. Use it to ask questions or request changes.' },
              ].map(({ title, desc }) => (
                <div key={title} className="flex gap-3">
                  <ChevronRight className="h-4 w-4 text-[#2B8FD8] mt-0.5 shrink-0" />
                  <div><strong className="text-slate-900">{title}:</strong> <span className="text-slate-600">{desc}</span></div>
                </div>
              ))}
            </div>

            {/* ── SECTION 9: Audience Views ── */}
            <SectionHeading id="audience" icon={Layers} title="Audience Views (Customer & Internal)" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Every document can have three versions: the full technical view, a customer-facing version, and an internal/technical deep-dive. The AI generates these on-demand when you first click each tab.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[
                { tab: 'Full View', color: 'bg-blue-50 border-blue-200', desc: 'The complete AI-generated documentation including all technical details, architecture decisions, and code references.' },
                { tab: 'Customer View', color: 'bg-emerald-50 border-emerald-200', desc: 'A simplified, non-technical version suitable for sharing with clients. Jargon is minimised, use-cases are emphasised.' },
                { tab: 'Internal / Technical', color: 'bg-amber-50 border-amber-200', desc: 'A deep-dive for engineers with full implementation details, code snippets, edge cases, and infrastructure notes.' },
              ].map(({ tab, color, desc }) => (
                <div key={tab} className={`border-2 ${color} rounded-xl p-4`}>
                  <h3 className="font-semibold text-slate-900 mb-2">{tab}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <Callout type="info" title="Amber dot indicator">
              An amber dot (●) on the Customer or Internal tab means the content is stale — the main documentation has been updated since this audience version was generated. Click the tab to regenerate it (takes 30–60 seconds).
            </Callout>
            <Callout type="tip" title="Audience-specific exports">
              When you export to Word or PDF while on the Customer or Internal tab, the exported file will contain the audience-specific content with the appropriate title suffix (e.g., "API Guide (Customer)").
            </Callout>

            {/* ── SECTION 10: AI Chat ── */}
            <SectionHeading id="ai-chat" icon={MessageSquare} title="AI Chat Assistant" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The AI assistant is embedded in every project's documentation page. It uses RAG (Retrieval-Augmented Generation) to answer questions based on your actual documentation and source code.
            </p>
            <MockupFrame title="projects/[id] → AI Panel">
              <MockChatPanel />
            </MockupFrame>
            <Step number={1} title="Open the AI panel">
              Click the <strong>✨ sparkle button</strong> floating in the bottom-right corner of the documentation page.
            </Step>
            <Step number={2} title="Select your mode">
              <strong>Ask Questions</strong> — for Q&A about the documentation. Available to all users.<br />
              <strong>Request Changes</strong> — for asking the AI to improve or modify the documentation (Editor/Admin only).
            </Step>
            <Step number={3} title="Use Common Questions (if available)">
              If other team members have previously asked questions about this project, the top of the chat panel shows a <em>Common Questions</em> section. Click any question to send it instantly.
            </Step>
            <Step number={4} title="Ask your question">
              Type your question in the input box and press Enter or click Send. The AI will search through the documentation and provide a contextual answer with confidence score.
            </Step>
            <Callout type="info" title="Sources are shown">
              After each AI answer, you'll see which document sections it referenced (e.g. "Source: Section 3.1 — Authentication"). This helps you verify the AI's answer against the original documentation.
            </Callout>

            {/* ── SECTION 11: Suggest Changes ── */}
            <SectionHeading id="suggest" icon={Sparkles} title="Requesting AI Documentation Changes" roles={['editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Editors and Admins can use the AI to suggest improvements to the documentation. Instead of editing manually, you describe what you want changed and the AI proposes new content.
            </p>
            <Step number={1} title="Open the AI panel and switch to 'Request Changes' mode">
              Click the ✨ button, then click the <strong>Request Changes</strong> tab.
            </Step>
            <Step number={2} title="Describe the change you want">
              Type a clear instruction such as: <em>"Add a section about error handling for 429 rate limit responses"</em> or <em>"Rewrite the authentication section to be more concise."</em>
            </Step>
            <Step number={3} title="Review the suggestion">
              The AI will generate a suggested revision with an explanation of what changed. Review it carefully before applying.
            </Step>
            <Step number={4} title="Apply or discard">
              Click <strong>Apply Changes</strong> to update the documentation, or discard to start over. Applied changes are tracked in version history.
            </Step>
            <Callout type="warning" title="Concurrency protection">
              If another editor modified the document while you were reviewing the AI suggestion, you'll see a conflict error. Refresh the page to get the latest version before applying changes.
            </Callout>

            {/* ── SECTION 12: Source Files ── */}
            <SectionHeading id="source-files" icon={Code} title="Source Files Browser" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              After importing source code, you can browse the original files that were analysed to generate the documentation. This provides full traceability from documentation back to source code.
            </p>
            <Step number={1} title="Open Source Files panel">
              In the project detail page toolbar, click the <strong>Source</strong> button (visible when source files exist). A slide-out panel opens from the right.
            </Step>
            <Step number={2} title="Browse the file tree">
              Files are organised in a collapsible folder tree. You can see the file type, category (code/config/test/docs), and size for each file. Use the search box to filter files by name.
            </Step>
            <Step number={3} title="View a file's content">
              Click any file to view its full content in a syntax-highlighted code viewer with line numbers.
            </Step>
            <Step number={4} title="Check category breakdown">
              At the top of the panel, a summary shows the breakdown by category (e.g., "14 code files, 3 config files, 2 test files").
            </Step>
            <Callout type="tip" title="Great for verification">
              Use the source files browser to verify that the AI analysed the right files and didn't miss anything important in your codebase.
            </Callout>

            {/* ── SECTION 13: Version History ── */}
            <SectionHeading id="versions" icon={GitBranch} title="Version History & Diff View" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Every content change creates a version snapshot. You can compare any two snapshots side-by-side to see exactly what changed. The Version History panel also shows the regeneration chain across different document versions.
            </p>
            <Step number={1} title="Open Version History">
              Click the <strong>History</strong> button in the document toolbar. A panel slides in showing all version snapshots.
            </Step>
            <Step number={2} title="Browse snapshots">
              Each snapshot shows the timestamp, who made the change, and a brief summary. The current version is highlighted.
            </Step>
            <Step number={3} title="Compare two versions">
              Select any two versions using the checkboxes, then click <strong>Compare</strong>. The diff view shows additions in green and deletions in red (standard unified diff format).
            </Step>
            <Step number={4} title="View regeneration history">
              Switch to the <strong>Regenerations</strong> tab to see the full regeneration chain — which documents replaced which, and when.
            </Step>
            <Callout type="info" title="Superseded documents">
              When a document is regenerated, the old document is marked as "superseded" in the project sidebar. The latest version is auto-selected when you open the project.
            </Callout>

            {/* ── SECTION 14: Search ── */}
            <SectionHeading id="search" icon={Search} title="Search & Deep Links" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The Search page uses AI-powered semantic search to find relevant documentation across all projects. Unlike keyword search, it understands meaning — so searching "retry logic" will find docs that discuss "exponential backoff" even if they don't use the exact words.
            </p>
            <MockupFrame title="search">
              <MockSearchPage />
            </MockupFrame>
            <Step number={1} title="Navigate to Search">
              Click <strong>Search</strong> in the sidebar, or use the keyboard shortcut if available.
            </Step>
            <Step number={2} title="Type your query">
              Enter a natural language question or keywords (e.g., "how does authentication work" or "database connection pooling").
            </Step>
            <Step number={3} title="Review results">
              Each result shows the document title, the matched section heading, and a snippet of the matching text. The section name is shown in blue.
            </Step>
            <Step number={4} title="Click to deep-link">
              Clicking a result opens the project detail page and automatically scrolls to the matched section. No more hunting through a long document!
            </Step>
            <Callout type="tip" title="Search reveals documentation gaps">
              If your search returns no results, it means there's a gap in your documentation. This data is captured in the Analytics dashboard as "Documentation Gaps" (Admin only).
            </Callout>

            {/* ── SECTION 15: Feedback ── */}
            <SectionHeading id="feedback" icon={ThumbsUp} title="Providing Section Feedback" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              You can submit structured feedback on specific sections of a document. Feedback is stored and automatically injected into the next regeneration prompt — so the AI learns from your corrections.
            </p>
            <Step number={1} title="Hover over a heading">
              On the documentation page, hover over any heading (H1, H2, H3). A small feedback icon appears to the right.
            </Step>
            <Step number={2} title="Click the feedback icon">
              A popover appears with a section pre-filled. Choose a feedback type: <em>Inaccurate</em>, <em>Missing info</em>, <em>Unclear</em>, <em>Looks good</em>, or <em>General</em>.
            </Step>
            <Step number={3} title="Write your comment">
              Add a comment describing the issue or what's missing (e.g., "The retry logic here is wrong — it uses exponential backoff, not linear").
            </Step>
            <Step number={4} title="Submit">
              Click Submit. The feedback appears in the Feedback Panel (click the Feedback button in the toolbar).
            </Step>
            <Step number={5} title="Track feedback status">
              In the Feedback Panel, admins and editors can acknowledge or resolve feedback. Open feedback count is shown as a badge on the Feedback toolbar button.
            </Step>
            <Callout type="tip" title="Feedback is used in regeneration">
              When you regenerate a document, all open feedback is automatically included in the AI prompt as "IMPORTANT FEEDBACK FROM REVIEWERS". After successful regeneration, all injected feedback is marked as "acknowledged".
            </Callout>

            {/* ── SECTION 16: Export ── */}
            <SectionHeading id="export" icon={Download} title="Exporting Documents" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Documentation can be exported to Word (.docx) or PDF formats. Both support audience-specific content — the export reflects whichever view tab you're currently on.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                { fmt: 'Word (.docx)', icon: FileText, desc: 'Full markdown → Word conversion with proper headings, code blocks, tables, and lists. Best for sharing in Microsoft Office environments.' },
                { fmt: 'PDF', icon: FileText, desc: 'Clean, print-ready PDF with proper typography. Best for sending to clients or archiving a snapshot of documentation.' },
              ].map(({ fmt, icon: Icon, desc }) => (
                <div key={fmt} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-[#2B8FD8]" />
                    <h3 className="font-semibold text-slate-900">{fmt}</h3>
                  </div>
                  <p className="text-sm text-slate-600">{desc}</p>
                </div>
              ))}
            </div>
            <Step number={1} title="Select the right tab first">
              If you want customer-specific documentation, click the <strong>Customer View</strong> tab. For technical deep-dives, use <strong>Internal/Technical</strong>.
            </Step>
            <Step number={2} title="Click Word or PDF in the toolbar">
              Click the <strong>Word</strong> or <strong>PDF</strong> button. The file downloads automatically. The filename includes the audience suffix (e.g., "API_Guide_(Customer).pdf").
            </Step>
            <Callout type="info" title="Audience-specific exports">
              Exporting from the Customer View tab automatically exports the customer-specific version of the documentation (not the full technical version). Switch tabs to control which version is exported.
            </Callout>

            {/* ── SECTION 17: Webhooks ── */}
            <SectionHeading id="webhooks" icon={Webhook} title="Webhook Auto-Sync" roles={['editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Webhooks keep your documentation automatically updated whenever code is pushed to your Git repository. Supported providers: <strong>Azure DevOps</strong>, <strong>GitHub</strong>, <strong>Bitbucket</strong>, and <strong>GitLab</strong>.
            </p>
            <Step number={1} title="Open the Webhook Panel">
              On the Project Detail page, click the <strong>Auto-Sync</strong> button (green outline, webhook icon) in the toolbar.
            </Step>
            <Step number={2} title="Click 'Add Webhook'">
              In the panel, click the green <strong>+ Add Webhook</strong> button.
            </Step>
            <Step number={3} title="Fill in the webhook form">
              Select the document to update, enter the repository URL, your PAT token, and the branch to watch (e.g., <code className="bg-slate-100 px-1 rounded text-xs">refs/heads/main</code>).
            </Step>
            <Step number={4} title="Click 'Create Webhook'">
              On success, a webhook URL is generated and setup instructions appear automatically.
            </Step>
            <Step number={5} title="Configure your Git provider">
              Follow the displayed instructions to add the webhook URL to your repository's push events (the exact steps vary by provider — Azure DevOps, GitHub, Bitbucket, GitLab instructions are each shown).
            </Step>
            <Step number={6} title="Monitor activity">
              Switch to the <strong>Activity</strong> tab in the webhook panel to see a log of all webhook events, their status, and the branch/commit that triggered them.
            </Step>
            <Callout type="info" title="Quality drift warning">
              If a document has been auto-patched 10 or more times since the last full regeneration, an amber warning banner appears on the documentation page. Regenerating creates a clean, consolidated version.
            </Callout>
            <Callout type="warning" title="Keep your webhook URL secret">
              The webhook URL contains a unique token. Anyone with this URL can trigger documentation updates. Treat it like a password — don't share it publicly.
            </Callout>

            {/* ── SECTION 18: Analytics ── */}
            <SectionHeading id="analytics" icon={BarChart3} title="Analytics Dashboard" roles={['admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The Analytics Dashboard (Admin only) provides visibility into how the platform is being used. It tracks document views, search queries, AI chat usage, export activity, and documentation generation metrics.
            </p>
            <MockupFrame title="analytics">
              <MockAnalytics />
            </MockupFrame>
            <div className="space-y-3 mb-4">
              {[
                { metric: 'Total Events', desc: 'All tracked platform activity in the selected period.' },
                { metric: 'Top Viewed Documents', desc: 'Which documents are being read most — useful for identifying your most important documentation.' },
                { metric: 'Documentation Gaps', desc: 'Search queries that returned zero results — these highlight topics that need new documentation.' },
                { metric: 'AI Chat Usage', desc: 'AI query volume by project — shows which projects generate the most questions from users.' },
                { metric: 'Export Activity', desc: 'How many exports happened, broken down by format (Word vs PDF).' },
                { metric: 'Doc Generation Stats', desc: 'Success/failure rates and average processing time for documentation generation jobs.' },
              ].map(({ metric, desc }) => (
                <div key={metric} className="flex gap-3">
                  <ChevronRight className="h-4 w-4 text-[#2B8FD8] mt-0.5 shrink-0" />
                  <div><strong className="text-slate-900">{metric}:</strong> <span className="text-slate-600">{desc}</span></div>
                </div>
              ))}
            </div>
            <Callout type="admin" title="Admin only">
              The Analytics page is only visible and accessible to Admin users. The nav link appears under the "Administration" section in the sidebar.
            </Callout>

            {/* ── SECTION 19: Admin Panel ── */}
            <SectionHeading id="admin" icon={ShieldCheck} title="Admin Panel" roles={['admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              The Admin Panel provides tools for managing users and reviewing documentation delete requests.
            </p>
            <MockupFrame title="admin">
              <MockAdminPanel />
            </MockupFrame>
            <h3 className="font-semibold text-slate-900 mt-6 mb-3">Users Tab</h3>
            <Step number={1} title="View all users">
              The Users tab lists every registered user with their name, email, and current role.
            </Step>
            <Step number={2} title="Change a user's role">
              In the role dropdown next to a user's name, select the new role (Reader / Editor / Admin). The change takes effect immediately.
            </Step>
            <Step number={3} title="Reset a user's password">
              Click the Reset Password button next to a user. A temporary password is generated and displayed — share it securely with the user. They should change it immediately.
            </Step>
            <Callout type="warning" title="You cannot change your own role">
              Admins are protected from accidentally removing their own admin access. Changing your own role will return an error.
            </Callout>
            <h3 className="font-semibold text-slate-900 mt-6 mb-3">Delete Requests Tab</h3>
            <Step number={1} title="Review pending requests">
              Editors cannot delete documents directly — they submit a delete request with a reason. Admins review these requests here.
            </Step>
            <Step number={2} title="Approve or Reject">
              Click <strong>Approve</strong> to permanently delete the document, or <strong>Reject</strong> to keep it. Rejected requests remain in the list for auditing.
            </Step>

            {/* ── SECTION 20: Settings ── */}
            <SectionHeading id="settings" icon={Settings} title="Settings & Profile" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-4">
              Access your settings by clicking <strong>Settings</strong> in the bottom of the sidebar. The settings page has three tabs.
            </p>
            <div className="space-y-4 mb-6">
              {[
                { tab: 'Profile', desc: 'Update your display name and email address. Changes take effect immediately.', icon: Users },
                { tab: 'Security', desc: 'Change your password. You must enter your current password to set a new one.', icon: Lock },
                { tab: 'Saved Tokens', desc: 'View and manage your saved repository tokens (GitHub/GitLab/Azure DevOps/Bitbucket). Tokens are encrypted and stored per-user. Delete tokens you no longer need.', icon: GitBranch },
              ].map(({ tab, desc, icon: Icon }) => (
                <div key={tab} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{tab}</p>
                    <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Callout type="tip" title="Saved tokens are automatically managed">
              When you import a repo using a token, it's saved automatically. On future imports from the same repo, leave the token field blank and the saved token will be reused. Manage saved tokens in Settings → Saved Tokens.
            </Callout>

            {/* ── SECTION 21: Quick Reference ── */}
            <SectionHeading id="quick-ref" icon={HelpCircle} title="Quick Reference by Role" roles={['reader', 'editor', 'admin']} />
            <p className="text-slate-600 leading-relaxed mb-6">Use this summary to quickly find what you can do based on your role.</p>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Action</th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700"><RoleBadge role="reader" /></th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700"><RoleBadge role="editor" /></th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700"><RoleBadge role="admin" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['View documentation', true, true, true],
                    ['Use AI chat (Q&A)', true, true, true],
                    ['Search documentation', true, true, true],
                    ['Browse source files', true, true, true],
                    ['View version history', true, true, true],
                    ['Submit section feedback', true, true, true],
                    ['Export to Word / PDF', true, true, true],
                    ['Add customers & projects', false, true, true],
                    ['Upload / import documentation', false, true, true],
                    ['Request AI changes', false, true, true],
                    ['Configure webhooks', false, true, true],
                    ['Request document deletion', false, true, true],
                    ['Delete documents directly', false, false, true],
                    ['Manage user roles', false, false, true],
                    ['Approve delete requests', false, false, true],
                    ['View analytics dashboard', false, false, true],
                    ['Reset user passwords', false, false, true],
                  ].map(([action, reader, editor, admin]) => (
                    <tr key={action} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-700">{action}</td>
                      <td className="px-3 py-2.5 text-center">{reader ? <CheckCircle className="h-4 w-4 text-emerald-500 mx-auto" /> : <div className="h-4 w-4 rounded-full border-2 border-slate-200 mx-auto" />}</td>
                      <td className="px-3 py-2.5 text-center">{editor ? <CheckCircle className="h-4 w-4 text-emerald-500 mx-auto" /> : <div className="h-4 w-4 rounded-full border-2 border-slate-200 mx-auto" />}</td>
                      <td className="px-3 py-2.5 text-center">{admin ? <CheckCircle className="h-4 w-4 text-emerald-500 mx-auto" /> : <div className="h-4 w-4 rounded-full border-2 border-slate-200 mx-auto" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-16 pt-8 border-t border-slate-200 text-center">
              <Cloud className="h-8 w-8 text-[#2B8FD8] mx-auto mb-2" />
              <p className="text-slate-500 text-sm">CloudFactory Docs Hub · User Guide</p>
              <p className="text-slate-400 text-xs mt-1">For technical support, contact your system administrator.</p>
              <div className="flex justify-center gap-4 mt-4">
                <Link to="/dashboard" className="text-[#2B8FD8] text-sm hover:underline flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" /> Go to Dashboard
                </Link>
              </div>
            </div>

          </main>
        </div>
      </div>
    </Layout>
  );
};

export default UserGuidePage;
